import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import MajorCombobox from "@/components/MajorCombobox";
import { Input } from "@/components/ui/input";
import { fetchMajorsList, updateMyProfile } from "@/services/profile";
import type { ProfilePayload } from "@/services/profile";
import { OnboardingQuestion, SelectionSection } from "./OnboardingQuestion";
import type { OnboardingStepProps } from "./types";

export default function AcademicsStep({
  profile,
  onValidityChange,
  registerSave,
  updateProfile,
}: OnboardingStepProps) {
  const [weightedGpa, setWeightedGpa] = useState(
    profile.weightedGpa != null ? String(profile.weightedGpa) : ""
  );
  const [satScore, setSatScore] = useState(
    profile.satScore != null ? String(profile.satScore) : ""
  );
  const [actScore, setActScore] = useState(
    profile.actScore != null ? String(profile.actScore) : ""
  );
  const [majors, setMajors] = useState<string[]>(profile.preferredMajors ?? []);
  const [allMajors, setAllMajors] = useState<string[]>([]);

  useEffect(() => {
    fetchMajorsList()
      .then(setAllMajors)
      .catch(() => setAllMajors([]));
  }, []);
  useEffect(() => onValidityChange(true), [onValidityChange]);

  const valuesRef = useRef({ weightedGpa, satScore, actScore, majors });
  valuesRef.current = { weightedGpa, satScore, actScore, majors };

  useEffect(() => {
    registerSave(async () => {
      const values = valuesRef.current;
      const payload: ProfilePayload = {
        preferredMajors: values.majors,
        weightedGpa: values.weightedGpa.trim() ? Number(values.weightedGpa) : null,
      };
      if (values.satScore) payload.satScore = Number(values.satScore);
      if (values.actScore) payload.actScore = Number(values.actScore);

      try {
        await updateMyProfile(payload);
        updateProfile?.({
          weightedGpa: payload.weightedGpa,
          satScore: payload.satScore ?? null,
          actScore: payload.actScore ?? null,
          preferredMajors: values.majors,
        });
        return true;
      } catch (error: any) {
        toast.error(String(error?.message || "Check the academic details and try again"));
        return false;
      }
    });
    return () => registerSave(null);
  }, [registerSave, updateProfile]);

  const addMajor = (major: string) => {
    if (!major || majors.includes(major) || majors.length >= 3) return;
    setMajors(previous => [...previous, major]);
  };

  return (
    <OnboardingQuestion
      eyebrow="Academics"
      title="What academic details should I use?"
      description="Add what’s available today. Every field is optional and can be updated later."
      coachCopy="These details help me find schools that fit in the classroom as well as on the course."
      coachBadge="A+"
    >
      <div className="space-y-7">
        <SelectionSection
          label="Weighted GPA"
          help="Enter the weighted GPA shown on the transcript. Skip it if the school doesn’t report one."
        >
          <Input
            className="h-12 max-w-sm rounded-xl border-border-strong bg-white px-4 text-forest shadow-sm focus-visible:ring-primary"
            placeholder="e.g. 4.3"
            value={weightedGpa}
            onChange={event => setWeightedGpa(event.target.value)}
            inputMode="decimal"
            aria-label="Weighted GPA"
          />
        </SelectionSection>

        <SelectionSection
          label="Possible majors"
          help="Choose up to three. It’s completely fine not to know yet."
        >
          {majors.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {majors.map(major => (
                <span
                  key={major}
                  className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-lime-soft px-3 py-1.5 text-xs font-bold text-forest"
                >
                  {major}
                  <button
                    type="button"
                    aria-label={`Remove ${major}`}
                    onClick={() => setMajors(values => values.filter(value => value !== major))}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {majors.length < 3 && !majors.includes("Undecided") && (
            <div>
              <MajorCombobox
                majors={allMajors}
                onChange={addMajor}
                exclude={majors}
                placeholder="Search majors…"
                className="h-12 border-border-strong"
              />
              <button
                type="button"
                onClick={() => setMajors(["Undecided"])}
                className="mt-2 text-xs font-bold text-muted-foreground underline decoration-border-strong underline-offset-4 transition hover:text-forest"
              >
                Not sure yet — mark as undecided
              </button>
            </div>
          )}
        </SelectionSection>

        <SelectionSection label="Test scores" help="Add either score, both, or leave them blank.">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="rounded-2xl border-2 border-border bg-white p-4 shadow-[0_3px_0_#d7e2d9]">
              <span className="mb-2 block text-xs font-extrabold text-forest-soft">SAT score</span>
              <Input
                aria-label="SAT score"
                value={satScore}
                onChange={event => setSatScore(event.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="400–1600"
                inputMode="numeric"
                className="h-11 border-border-strong"
              />
            </label>
            <label className="rounded-2xl border-2 border-border bg-white p-4 shadow-[0_3px_0_#d7e2d9]">
              <span className="mb-2 block text-xs font-extrabold text-forest-soft">ACT score</span>
              <Input
                aria-label="ACT score"
                value={actScore}
                onChange={event => setActScore(event.target.value.replace(/\D/g, "").slice(0, 2))}
                placeholder="1–36"
                inputMode="numeric"
                className="h-11 border-border-strong"
              />
            </label>
          </div>
        </SelectionSection>
      </div>
    </OnboardingQuestion>
  );
}
